// RiskWidget — live risk scoring during subscriber-add.
//
// Debounces input changes and calls risk.score_for_input. Renders score +
// class chip + the top 3 contributing features for transparency. The
// "Use this risk" button bubbles the computed class up so the form can
// auto-populate its risk_classification field.

import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

export interface RiskFeatures {
  age_years?: number;
  occupation?: string;
  district?: string;
  has_aadhaar?: boolean;
  has_pan?: boolean;
  has_guarantor?: boolean;
  prior_chits_count?: number;
  prior_default_count?: number;
  prior_late_payment_rate?: number;
}

interface ScoreResult {
  score: number;
  risk_class: 'low' | 'medium' | 'high';
  contributors: Array<{ feature: string; weight: number; reason: string }>;
  model_version: string;
}

interface Props {
  features: RiskFeatures;
  on_apply_class: (cls: 'low' | 'medium' | 'high') => void;
}

function features_signature(f: RiskFeatures): string {
  // Stable-ordered JSON for the debounce keying
  const keys = Object.keys(f).sort();
  const obj: Record<string, unknown> = {};
  for (const k of keys) obj[k] = (f as Record<string, unknown>)[k];
  return JSON.stringify(obj);
}

function has_any_signal(f: RiskFeatures): boolean {
  return (
    typeof f.age_years === 'number' ||
    !!f.occupation ||
    !!f.district ||
    f.has_aadhaar !== undefined ||
    f.has_pan !== undefined ||
    f.has_guarantor !== undefined
  );
}

export function RiskWidget({ features, on_apply_class }: Props): ReactElement | null {
  const { t } = useTranslation();
  const [result, set_result] = useState<ScoreResult | null>(null);
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  const sig = features_signature(features);
  useEffect(() => {
    if (!has_any_signal(features)) {
      set_result(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      set_busy(true);
      set_error(null);
      try {
        const r = await orpc.risk.score_for_input({ features });
        if (!cancelled) set_result(r as ScoreResult);
      } catch (e) {
        if (!cancelled) set_error(friendly_error(e));
      } finally {
        if (!cancelled) set_busy(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps — sig is the canonical input key
  }, [sig]);

  if (!has_any_signal(features) && !result) return null;
  // AI risk MCP is optional; if it errors (service down, unavailable, etc.)
  // collapse the widget rather than show a useless "Something went wrong"
  // panel — the operator has manual risk buttons right below.
  if (error && !result) return null;

  return (
    <div className="bg-surface border border-line rounded-lg p-4">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs font-medium text-ink/70">
          {t('risk.title', 'Default risk (AI)')}
        </span>
        {busy && <span className="text-[10px] text-ink/50">{t('risk.calculating', 'Calculating…')}</span>}
      </div>
      {result && (
        <>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-3xl font-mono">{result.score}</span>
            <span className="text-xs text-ink/50">/ 100</span>
            <RiskChip cls={result.risk_class} />
            <button
              type="button"
              onClick={() => on_apply_class(result.risk_class)}
              className="ml-auto text-[11px] underline text-primary"
            >
              {t('risk.apply', 'Use this')}
            </button>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-ink/50">
              {t('risk.contributors', 'Top factors')}
            </div>
            {result.contributors.slice(0, 3).map((c) => (
              <div key={c.feature} className="flex items-start justify-between text-xs gap-2">
                <span className="text-ink/80">{c.reason}</span>
                <span
                  className={`font-mono shrink-0 ${
                    c.weight > 0 ? 'text-bad' : c.weight < 0 ? 'text-good' : 'text-ink/50'
                  }`}
                >
                  {c.weight > 0 ? '+' : ''}
                  {c.weight}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-ink/40 font-mono">
            {result.model_version}
          </div>
        </>
      )}
    </div>
  );
}

function RiskChip({ cls }: { cls: 'low' | 'medium' | 'high' }): ReactElement {
  const styles =
    cls === 'high'
      ? 'bg-bad-soft text-bad'
      : cls === 'medium'
        ? 'bg-warn-soft text-warn'
        : 'bg-good-soft text-good';
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${styles}`}>{cls}</span>;
}
